/* Stile Donna — Prestige look: small, dependency-free components for sdc-p-* sections. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Reveal on scroll */
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); } });
      }, { rootMargin: '0px 0px -8% 0px' })
    : null;
  const observeReveals = (root = document) => {
    root.querySelectorAll('.p-reveal:not(.is-visible)').forEach((el) => (io ? io.observe(el) : el.classList.add('is-visible')));
  };

  /* Autoplay waits for the visitor's first interaction (scroll, tap, key).
     Until then the first slide stays put, so a later slide can never become
     the page's "largest content" and delay LCP. In the theme editor it runs normally. */
  const WAKE_EVENTS = ['scroll', 'wheel', 'pointerdown', 'keydown', 'touchstart'];
  let awake = Boolean(window.Shopify && window.Shopify.designMode);
  const sleepers = new Set();
  const wake = () => {
    if (awake) return;
    awake = true;
    WAKE_EVENTS.forEach((t) => window.removeEventListener(t, wake, { capture: true }));
    sleepers.forEach((s) => s.restart());
    sleepers.clear();
  };
  if (!awake) WAKE_EVENTS.forEach((t) => window.addEventListener(t, wake, { capture: true, passive: true }));

  /* Slideshow: <p-slideshow data-autoplay="6"> with [data-slide] children, optional [data-dot], [data-prev], [data-next]; swipe on touch */
  class PSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll(':scope [data-slide]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.index = 0;
      if (this.slides.length < 2) { this.slides[0]?.classList.add('is-active'); return; }
      this.ac = new AbortController();
      const o = { signal: this.ac.signal };
      this.dots.forEach((d, i) => d.addEventListener('click', () => { this.go(i); this.restart(); }, o));
      this.querySelector('[data-prev]')?.addEventListener('click', () => { this.go(this.index - 1); this.restart(); }, o);
      this.querySelector('[data-next]')?.addEventListener('click', () => { this.go(this.index + 1); this.restart(); }, o);
      let x0 = null, y0 = 0;
      this.addEventListener('pointerdown', (e) => { if (e.pointerType === 'mouse') return; x0 = e.clientX; y0 = e.clientY; }, o);
      this.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0, dy = e.clientY - y0; x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { this.go(this.index + (dx < 0 ? 1 : -1)); this.restart(); }
      }, o);
      this.addEventListener('pointercancel', () => { x0 = null; }, o);
      this.addEventListener('shopify:block:select', (e) => { const i = this.slides.findIndex((s) => s.contains(e.target)); if (i > -1) { this.go(i); this.stop(); } }, o);
      this.go(0);
      this.ready = true;
      this.restart();
    }
    disconnectedCallback() { this.ac?.abort(); this.stop(); sleepers.delete(this); }
    go(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      if (this.ready) this.classList.add('has-moved');
      this.slides.forEach((s, n) => { s.classList.toggle('is-active', n === this.index); s.setAttribute('aria-hidden', n === this.index ? 'false' : 'true'); s.inert = n !== this.index; });
      this.dots.forEach((d, n) => d.setAttribute('aria-current', n === this.index ? 'true' : 'false'));
      this.dispatchEvent(new CustomEvent('p:change', { detail: this.index }));
    }
    stop() { clearInterval(this.timer); }
    restart() {
      this.stop();
      const s = parseInt(this.dataset.autoplay || '0', 10);
      if (s > 0 && !reduce) {
        if (!awake) { sleepers.add(this); return; }
        this.timer = setInterval(() => this.go(this.index + 1), s * 1000);
      }
    }
  }

  /* Tabs: <p-tabs> with [data-tab="i"] buttons and [data-panel="i"] panels */
  class PTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.tabs.forEach((t) => t.addEventListener('click', () => this.show(t.dataset.tab)));
      this.tabs.forEach((t) => t.addEventListener('keydown', (e) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
        const i = this.tabs.indexOf(t) + (e.key === 'ArrowRight' ? 1 : -1);
        const next = this.tabs[(i + this.tabs.length) % this.tabs.length];
        next.focus(); this.show(next.dataset.tab);
      }));
    }
    show(id) {
      this.tabs.forEach((t) => { const on = t.dataset.tab === id; t.setAttribute('aria-selected', on); t.tabIndex = on ? 0 : -1; });
      this.panels.forEach((p) => { p.hidden = p.dataset.panel !== id; });
    }
  }

  /* Before / after comparison: <p-compare> with input[type=range] */
  class PCompare extends HTMLElement {
    connectedCallback() {
      const r = this.querySelector('input[type=range]');
      const set = () => this.style.setProperty('--p-pos', r.value + '%');
      r?.addEventListener('input', set); set();
    }
  }

  /* Countdown: <p-countdown data-end="2026-12-31T23:59"> */
  class PCountdown extends HTMLElement {
    connectedCallback() {
      const end = new Date(this.dataset.end).getTime();
      if (Number.isNaN(end)) { this.hidden = true; return; }
      const f = (n) => String(n).padStart(2, '0');
      const tick = () => {
        let s = Math.max(0, Math.floor((end - Date.now()) / 1000));
        const d = Math.floor(s / 86400); s -= d * 86400;
        const h = Math.floor(s / 3600); s -= h * 3600;
        const m = Math.floor(s / 60); s -= m * 60;
        const map = { d, h: f(h), m: f(m), s: f(s) };
        this.querySelectorAll('[data-unit]').forEach((el) => (el.textContent = map[el.dataset.unit]));
        if (end - Date.now() <= 0) clearInterval(this.t);
      };
      tick(); this.t = setInterval(tick, 1000);
    }
    disconnectedCallback() { clearInterval(this.t); }
  }

  /* Video modal: <p-video> button[data-open] + dialog */
  class PVideo extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.querySelector('[data-open]')?.addEventListener('click', () => {
        const tpl = this.querySelector('template');
        const box = this.dialog.querySelector('[data-frame]');
        box.innerHTML = ''; box.append(tpl.content.cloneNode(true));
        this.dialog.showModal();
      });
      this.dialog?.addEventListener('close', () => { this.dialog.querySelector('[data-frame]').innerHTML = ''; });
      this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      this.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
    }
  }

  /* Shop the look: <p-looks> wraps a p-slideshow; hotspots [data-hotspot] pick a product panel */
  class PLooks extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const h = e.target.closest('[data-hotspot]');
        if (!h) return;
        this.pick(h.dataset.hotspot);
      });
      this.querySelector('p-slideshow')?.addEventListener('p:change', (e) => {
        const first = this.querySelector(`[data-look="${e.detail}"] [data-hotspot]`);
        if (first) this.pick(first.dataset.hotspot);
      });
    }
    pick(id) {
      this.querySelectorAll('[data-hotspot]').forEach((h) => h.setAttribute('aria-pressed', h.dataset.hotspot === id));
      this.querySelectorAll('[data-look-product]').forEach((p) => { p.hidden = p.dataset.lookProduct !== id; });
    }
  }

  /* Row carousel: <p-carousel> with a scrollable [data-track] and [data-dir] arrow buttons */
  class PCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.btns = [...this.querySelectorAll(':scope > [data-dir]')];
      if (!this.track) return;
      this.ac = new AbortController();
      const o = { signal: this.ac.signal };
      this.btns.forEach((b) => b.addEventListener('click', () => {
        const item = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const step = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
        const per = Math.max(1, Math.round(this.track.clientWidth / step));
        this.track.scrollBy({ left: step * per * Number(b.dataset.dir), behavior: reduce ? 'auto' : 'smooth' });
      }, o));
      this.track.addEventListener('scroll', () => this.update(), { passive: true, signal: this.ac.signal });
      if ('ResizeObserver' in window) { this.ro = new ResizeObserver(() => this.update()); this.ro.observe(this.track); }
      this.update();
    }
    disconnectedCallback() { this.ac?.abort(); this.ro?.disconnect(); }
    update() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth - 2;
      this.toggleAttribute('data-scrollable', max > 0);
      const [prev, next] = [this.btns.find((b) => b.dataset.dir === '-1'), this.btns.find((b) => b.dataset.dir === '1')];
      if (prev) prev.disabled = t.scrollLeft <= 2;
      if (next) next.disabled = t.scrollLeft >= max;
    }
  }

  /* Product card: swipeable photos, arrows, dots, hover slides to the 2nd photo, quick-add panel */
  class PCard extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slides]');
      this.dots = [...this.querySelectorAll('.p-card__dots i')];
      this.ac = new AbortController();
      const o = { signal: this.ac.signal };
      if (this.track && this.track.children.length > 1) {
        this.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', (e) => {
          e.preventDefault(); this.moved = true; this.go(this.index() + Number(b.dataset.step));
        }, o));
        this.track.addEventListener('scroll', () => this.paint(), { passive: true, signal: this.ac.signal });
        this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse' && !this.moved && this.index() === 0) this.go(1); }, o);
        this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { this.moved = false; this.go(0); } }, o);
      }
      const panel = this.querySelector('[data-p-qa-panel]');
      const toggle = this.querySelector('[data-p-qa-toggle]');
      if (panel && toggle) {
        const set = (open) => { panel.hidden = !open; toggle.setAttribute('aria-expanded', open); this.classList.toggle('is-picking', open); };
        toggle.addEventListener('click', (e) => { e.preventDefault(); set(panel.hidden); if (!panel.hidden) panel.querySelector('button:not([disabled])')?.focus(); }, o);
        this.querySelector('[data-p-qa-close]')?.addEventListener('click', () => { set(false); toggle.focus(); }, o);
        this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) { set(false); toggle.focus(); } }, o);
        this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') set(false); }, o);
      }
    }
    disconnectedCallback() { this.ac?.abort(); }
    index() { const w = this.track.clientWidth || 1; return Math.round(this.track.scrollLeft / w); }
    go(i) {
      const n = this.track.children.length;
      const k = (i + n) % n;
      this.track.scrollTo({ left: k * this.track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    }
    paint() { const i = this.index(); this.dots.forEach((d, n) => d.classList.toggle('is-on', n === i)); }
  }

  /* Add to cart without leaving the page, then open Horizon's cart drawer */
  const addToCart = async (items, source) => {
    let Ev = null;
    try { ({ CartLinesUpdateEvent: Ev } = await import('@shopify/events')); } catch (e) { Ev = null; }
    const deferred = Ev?.createPromise?.();
    if (Ev && deferred) {
      (source || document).dispatchEvent(new Ev({
        action: 'add', context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      }));
    }
    const sections = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean).join(',');
    const url = (window.Theme?.routes?.cart_add_url) || '/cart/add.js';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items.map((i) => ({ id: Number(i.id), quantity: i.quantity })), sections }),
    }).then((r) => r.json());
    const cart = await fetch(((window.Theme?.routes?.cart_url) || '/cart') + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const didError = Boolean(res.status);
    const qty = items.reduce((a, i) => a + i.quantity, 0);
    if (deferred) {
      deferred.resolve({
        cart: Ev.createCartFromAjaxResponse ? Ev.createCartFromAjaxResponse(cart) : cart,
        detail: { items: cart.items, source: 'sdc-quick-add', sourceId: 'sdc-quick-add', itemCount: qty, sections: res.sections, didError },
      });
    }
    if (didError) throw new Error(res.description || res.message || 'Errore');
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    setTimeout(() => {
      if (drawer?.open && !drawer.isOpen) drawer.open();
      else if (!drawer) window.location.href = (window.Theme?.routes?.cart_url) || '/cart';
    }, 150);
    return cart;
  };
  window.SDC = Object.assign(window.SDC || {}, { addToCart });

  const busy = (btn, on) => { if (!btn) return; btn.toggleAttribute('aria-busy', on); btn.disabled = on; };
  const flash = (btn, ok, msg) => {
    if (!btn) return;
    btn.classList.add(ok ? 'is-added' : 'is-error');
    if (!ok && msg) btn.title = msg;
    setTimeout(() => btn.classList.remove('is-added', 'is-error'), 1600);
  };

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-p-add]');
    if (!btn) return;
    e.preventDefault();
    const sel = btn.dataset.pSelect ? document.getElementById(btn.dataset.pSelect) : null;
    const id = sel ? sel.value : btn.dataset.pAdd;
    if (!id) return;
    busy(btn, true);
    try {
      await addToCart([{ id, quantity: 1 }], btn);
      flash(btn, true);
      const card = btn.closest('p-card');
      const panel = card?.querySelector('[data-p-qa-panel]');
      if (panel) { panel.hidden = true; card.classList.remove('is-picking'); card.querySelector('[data-p-qa-toggle]')?.setAttribute('aria-expanded', 'false'); }
    } catch (err) { flash(btn, false, err.message); }
    busy(btn, false);
  });

  /* "Il prodotto della settimana" and any form marked data-p-cart-form: add via AJAX instead of going to /cart */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form.p-feat__form, form[data-p-cart-form]');
    if (!form) return;
    e.preventDefault();
    const fd = new FormData(form);
    const btn = form.querySelector('[type=submit]');
    busy(btn, true);
    try {
      await addToCart([{ id: fd.get('id'), quantity: Number(fd.get('quantity')) || 1 }], form);
      flash(btn, true);
    } catch (err) { flash(btn, false, err.message); }
    busy(btn, false);
  });

  const define = (n, c) => { if (!customElements.get(n)) customElements.define(n, c); };
  define('p-slideshow', PSlideshow);
  define('p-tabs', PTabs);
  define('p-compare', PCompare);
  define('p-countdown', PCountdown);
  define('p-video', PVideo);
  define('p-looks', PLooks);
  define('p-carousel', PCarousel);
  define('p-card', PCard);

  observeReveals();
  document.addEventListener('shopify:section:load', (e) => observeReveals(e.target));
})();
